import {
  GeoZoneDto,
  LocationDto,
  PlantDto,
  TicketGeozoneDto,
} from '@trucktrax/trucktrax-ts-common';

import { GEOZONE_TYPE } from '../constants/appConstants';
import { METERS_PER_MILE } from '../constants/mapConstants';
import { calculateDistanceInMeters } from './distanceUtil';
import { isLocationValid } from './geoUtil';

/**
 * Fallback geozone radius (meters) used only when a selected plant has no usable
 * geozone geometry — mirrors the backend's 0.1-mile default for address-derived zones.
 */
export const DEFAULT_DELIVERY_GEOZONE_RADIUS_METERS = Math.round(0.1 * METERS_PER_MILE);

// The stored zone `name` holds a GEOZONE_TYPE *value* ('plant' | 'inqueue' | 'return'),
// so membership must be checked against the values, not the keys.
const KNOWN_ZONE_NAMES: string[] = Object.values(GEOZONE_TYPE);

const lat = (p: LocationDto) => p.latitude ?? p.lat ?? 0;
const lng = (p: LocationDto) => p.longitude ?? p.lng ?? 0;

/**
 * Select a plant's primary delivery ("plant") geozone. Mirrors the selection rule in
 * `mapAccessorForPlantDetailGeozone`: prefer a geozone named `plant`; otherwise fall back
 * to any geozone whose name is not one of the known zone types (legacy plants named their
 * zone after the plant itself).
 */
export function findPlantDeliveryGeozone(plant?: PlantDto): GeoZoneDto | undefined {
  const geozones = plant?.geozones;
  if (!geozones || geozones.length < 1) {
    return undefined;
  }
  return (
    geozones.find((g: GeoZoneDto) => g.name === GEOZONE_TYPE.PLANT) ||
    // Legacy plants named their delivery zone after the plant itself. Fall back to any zone
    // whose name is NOT one of the known non-plant types (inqueue/return), never those.
    geozones.find((g: GeoZoneDto) => !g.name || !KNOWN_ZONE_NAMES.includes(g.name))
  );
}

/**
 * Approximate a polygon zone as a bounding circle: centroid of the vertices plus the radius
 * that reaches the farthest vertex. v2 ticket geozones are circle-only, so a polygon plant
 * zone cannot be copied verbatim (see DEV-2580 — polygon ticket support is deferred).
 */
function polygonToCircle(points: LocationDto[]): { center: LocationDto; radius: number } | null {
  const valid = points.filter(isLocationValid);
  if (valid.length === 0) {
    return null;
  }
  const center: LocationDto = {
    latitude: valid.reduce((sum, p) => sum + lat(p), 0) / valid.length,
    longitude: valid.reduce((sum, p) => sum + lng(p), 0) / valid.length,
  };
  const centerLatLng = { lat: lat(center), lng: lng(center) };
  const radius = valid.reduce((max, p) => {
    const distance = calculateDistanceInMeters(centerLatLng, { lat: lat(p), lng: lng(p) }) ?? 0;
    return distance > max ? distance : max;
  }, 0);
  return { center, radius: radius || DEFAULT_DELIVERY_GEOZONE_RADIUS_METERS };
}

/**
 * Build a ticket geozone (always a circle) from a plant a dispatcher selected as the new
 * delivery location. Circle plant zones are copied verbatim; polygon zones are approximated
 * as a bounding circle; if the plant has no zone geometry, falls back to the plant location
 * with a default radius. Returns null when the plant has no usable coordinates at all.
 */
export function plantToTicketGeozone(plant?: PlantDto): TicketGeozoneDto | null {
  if (!plant) {
    return null;
  }

  const geozone = findPlantDeliveryGeozone(plant);
  const circle = geozone?.zone?.circle;
  if (circle?.center && isLocationValid(circle.center)) {
    return { zone: { circle: { center: circle.center, radius: circle.radius } } };
  }

  const polygonPoints = geozone?.zone?.polygon?.points;
  if (polygonPoints && polygonPoints.length > 0) {
    const approximated = polygonToCircle(polygonPoints);
    if (approximated) {
      return { zone: { circle: approximated } };
    }
  }

  const { location } = plant;
  if (location && isLocationValid(location)) {
    return {
      zone: {
        circle: { center: location, radius: DEFAULT_DELIVERY_GEOZONE_RADIUS_METERS },
      },
    };
  }

  return null;
}

/**
 * Wrap a polygon drawn on the map into a circle ticket geozone (bounding circle of the
 * vertices). v2 tickets are circle-only, so a drawn polygon is approximated rather than
 * dropped. Returns null when there are no usable points.
 */
export function polygonPointsToTicketGeozone(points?: LocationDto[]): TicketGeozoneDto | null {
  if (!points || points.length === 0) {
    return null;
  }
  const circle = polygonToCircle(points);
  return circle ? { zone: { circle } } : null;
}

/**
 * Wrap a circle produced by the map (the "enter an address" path, where the dispatcher
 * geocodes/drops a point and draws a radius) into a ticket geozone. Returns null if the
 * circle is missing a valid center or a positive radius.
 */
export function circleToTicketGeozone(circle?: {
  center?: LocationDto;
  radius?: number;
}): TicketGeozoneDto | null {
  if (!circle?.center || !isLocationValid(circle.center) || !(circle.radius && circle.radius > 0)) {
    return null;
  }
  return { zone: { circle: { center: circle.center, radius: circle.radius } } };
}
